import { toJS } from "mobx";
import moment from "moment";

export function calculateProrationByExactDays({
  currentStartDate,
  currentEndDate,
  oldPriceMonthly,
  newPriceMonthly,
  oldCycleMonths = 1,
  newCycleMonths = 1,
  metadata,
  now = new Date(),
}) {
  const current = moment(now).startOf("day");
  const start = moment(currentStartDate).startOf("day");
  const end = moment(currentEndDate).startOf("day");

  if (current.isAfter(end)) {
    return {
      prorateCreditAmount: 0,
      prorateChargeAmount: 0,
      prorateAmount: 0,
      creditAmount: 0,
      chargeAmount: newPriceMonthly * newCycleMonths,
      netAmount: newPriceMonthly * newCycleMonths,
    };
  }
  const isCycleExtension = newCycleMonths > oldCycleMonths;

  const totalDaysOldCycle = Math.max(1, end.diff(start, "days"));
  const daysRemaining = Math.max(0, end.diff(current, "days"));

  const oldCycleTotal = oldPriceMonthly * oldCycleMonths;

  // Unused value of the current subscription
  const creditAmount = Math.round((oldCycleTotal / totalDaysOldCycle) * daysRemaining);

  let chargeAmount = 0;
  let prorateAmount = 0;
  let prorateChargeAmount = 0;
  let prorateCreditAmount = 0;
  let netAmount = 0;

  if (isCycleExtension) {
    // Example:
    // Current: 17 rooms @ 6000 = 102000/month
    // New:     18 rooms @ 6000 = 108000/month
    // Difference = 6000/month

    const monthlyDifference = Math.max(0, newPriceMonthly - oldPriceMonthly);

    // Full charge for the new cycle
    chargeAmount = newPriceMonthly * newCycleMonths;
    // Charge extra capacity for the remaining days
    prorateChargeAmount = Math.round((monthlyDifference / totalDaysOldCycle) * daysRemaining);
    netAmount = chargeAmount + prorateChargeAmount;
  } else {
    // const chargeAmountWP = metadata.newCapacity * metadata.newPlanPrice;
    const chargeAmountWP = newPriceMonthly * newCycleMonths;

    // Cost of the upgraded plan for the remaining days
    const newCycleTotalEquivalent = newPriceMonthly * oldCycleMonths;
    chargeAmount = Math.round((newCycleTotalEquivalent / totalDaysOldCycle) * daysRemaining);
    prorateAmount = creditAmount + (chargeAmountWP - chargeAmount);
    prorateCreditAmount = creditAmount;
    // prorateCreditAmount = creditAmount + (newBilling - chargeAmount);
    netAmount = Math.max(0, chargeAmount - creditAmount);
  }

  return {
    prorateCreditAmount,
    prorateChargeAmount,
    prorateAmount,
    creditAmount, // informational/reference
    chargeAmount,
    netAmount,
  };
}

// Experimenting
function _getRemainingRatio({ currentPeriodStart, currentPeriodEnd, effectiveDate = new Date() }) {
  const current = moment(effectiveDate);
  const start = moment(currentPeriodStart).startOf("day");
  const end = moment(currentPeriodEnd).endOf("day");

  const totalDays = Math.max(1, end.diff(start, "days"));
  const remainingDays = Math.max(0, end.diff(current, "days"));

  return remainingDays / totalDays;
}

function _determineEffectiveDate(currentValue, newValue, renewalDate = new Date()) {
  if (newValue > currentValue) {
    return {
      effectiveAt: new Date(),
      requiresProration: true,
      billingAction: "immediate",
    };
  }

  if (newValue < currentValue) {
    return {
      effectiveAt: renewalDate,
      requiresProration: false,
      billingAction: "schedule",
    };
  }

  return {
    effectiveAt: new Date(),
    requiresProration: false,
    billingAction: "none",
  };
}

/**
 * Step 1: Calculate total price of the subscription
 * @param {number} planPrice - Price per room per month
 * @param {number} roomCapacity - Number of rooms
 * @param {number} billingMonths - Number of months to bill for
 * @returns {number} Total price of the subscription
 */
function calculateSubscriptionValue({ planPrice, roomCapacity, billingMonths }) {
  return planPrice * roomCapacity * billingMonths;
}

/**
 * Step 2: Calculate proration value
 * @param {object} currentSubscription - Current subscription
 * @param {object} newSubscription - New subscription
 * @param {Date} currentPeriodStart - Start date of the current period
 * @param {Date} currentPeriodEnd - End date of the current period
 * @param {Date} effectiveDate - Effective date of the change
 * @returns {object} Proration value
 */
export function calculateProrationValue({
  currentPeriodStart,
  currentPeriodEnd,
  currentSubscription,
  newSubscription,
  effectiveDate = new Date(),
}) {
  const remainingRatio = _getRemainingRatio({ currentPeriodStart, currentPeriodEnd, effectiveDate });

  const currentValue = calculateSubscriptionValue({
    planPrice: currentSubscription.pricePerRoom,
    roomCapacity: currentSubscription.roomCapacity,
    billingMonths: currentSubscription.cycleMonths,
  });

  const newValue = calculateSubscriptionValue({
    planPrice: newSubscription.pricePerRoom,
    roomCapacity: newSubscription.roomCapacity,
    billingMonths: newSubscription.cycleMonths,
  });

  const currentRemainingValue = currentValue * remainingRatio; // unused credit
  const newRemainingValue = newValue * remainingRatio; // Cost of new subscription for remaining period
  const netAmount = newRemainingValue - currentRemainingValue;

  const action = _determineEffectiveDate(currentValue, newValue, effectiveDate);
  return {
    amountToCharge: netAmount > 0 ? Math.ceil(netAmount) : 0,
    creditAmount: netAmount < 0 ? Math.ceil(Math.abs(netAmount)) : 0,
    netAmount: Math.ceil(netAmount),
    metadata: {
      prorateCredit: Math.ceil(currentRemainingValue) + (newValue - currentValue),
      effectiveAt: action.effectiveAt,
      requiresProration: action.requiresProration,
      billingAction: action.billingAction,
    },
  };
}

function previewSubscriptionChange({
  currentPeriodStart,
  currentPeriodEnd,
  currentSubscription,
  newSubscription,
  renewalDate,
}) {
  const decision = _determineEffectiveDate(currentSubscription, newSubscription, renewalDate);

  if (!decision.requiresProration) {
    return {
      ...decision,

      amountToCharge: 0,
      creditAmount: 0,

      message: "Changes will take effect at renewal.",
    };
  }

  const proration = calculateProration({
    currentSubscription,
    newSubscription,
    currentPeriodStart,
    currentPeriodEnd,
  });

  return {
    ...decision,
    ...proration,
  };
}
